import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  InputAdornment,
  Chip,
  Skeleton,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type { Archetype, OperationTypeDefinition } from '@/graph/domain/types';
import { filterCatalog } from '@/api/catalogFilter';

interface OperationsToolboxProps {
  catalog: OperationTypeDefinition[];
  isLoading: boolean;
  archetypes: Archetype[];
  selectedId: string | null;
  onSelect: (entry: OperationTypeDefinition) => void;
}

export function OperationsToolbox({
  catalog,
  isLoading,
  archetypes,
  selectedId,
  onSelect,
}: OperationsToolboxProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(
    () => filterCatalog(catalog, { archetypes, searchQuery }),
    [catalog, archetypes, searchQuery],
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1.5 }}
      data-testid="operations-toolbox"
    >
      {/* Section heading — mirrors Angular's <h6>Operations Toolbox</h6> */}
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, mb: 1, color: '#204c77', fontSize: '0.85rem' }}
      >
        Operations Toolbox
      </Typography>

      {/* Search input — mirrors Angular's <input placeholder=" search "> */}
      <TextField
        size="small"
        placeholder="Search…"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        fullWidth
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 1 }}
        data-testid="toolbox-search"
      />

      <Divider sx={{ mb: 1 }} />

      {/* Operations list */}
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={32} />
          ))}
        </Box>
      ) : filtered.length === 0 ? (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ mt: 2, textAlign: 'center', fontStyle: 'italic' }}
        >
          No operations found
        </Typography>
      ) : (
        <List dense disablePadding sx={{ overflow: 'auto', flex: 1 }}>
          {filtered.map((entry) => {
            const isCustom = entry.subType === 'customOperation';
            const isSelected = entry.id === selectedId;

            return (
              <ListItemButton
                key={entry.id}
                selected={isSelected}
                onClick={() => onSelect(entry)}
                data-testid={`op-item-${entry.id}`}
                sx={{
                  borderRadius: 1,
                  mb: 0.25,
                  py: 0.5,
                  px: 1,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.8rem',
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? 'inherit' : 'text.primary',
                          flex: 1,
                          lineHeight: 1.4,
                        }}
                      >
                        {entry.operationName}
                      </Typography>

                      {/* "Custom" badge — mirrors Angular's badge-secondary span */}
                      {isCustom && (
                        <Chip
                          component="span"
                          label="Custom"
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 16,
                            fontSize: '0.6rem',
                            flexShrink: 0,
                            color: isSelected ? 'inherit' : 'text.secondary',
                            borderColor: isSelected ? 'primary.contrastText' : 'text.disabled',
                          }}
                        />
                      )}
                    </Box>
                  }
                  disableTypography
                />
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
}
